import { motion } from "motion/react";
import SEOHead from "@/components/SEOHead";

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const principles = [
  {
    number: "01",
    title: "State, not answers.",
    body: "Most tools produce an answer from a document. Lumient updates the operational record that document affects, so the next workflow starts from what is already known.",
  },
  {
    number: "02",
    title: "Evidence stays attached.",
    body: "A field is only useful if a team can trust where it came from. Every record Lumient maintains stays tied to the source evidence and workflow logic behind it.",
  },
  {
    number: "03",
    title: "Re-reading is failure.",
    body: "If a team has to interpret the same invoice, contract, or compliance packet every time a question comes up, the business state was never captured.",
  },
  {
    number: "04",
    title: "Agents need maintained state.",
    body: "Agents can help with document-heavy work only when they operate on maintained workflow state, not a fresh interpretation of raw files on every run.",
  },
];

// ---------------------------------------------------------------------------
// Motion
// ---------------------------------------------------------------------------

const EASE = [0.25, 0.1, 0.25, 1] as const;

const heroContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const heroItem = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
};

// ---------------------------------------------------------------------------
// Shared layout — wide container, tight gutters, consistent across all sections
// ---------------------------------------------------------------------------

const CONTAINER = "max-w-screen-2xl mx-auto px-6 lg:px-16";

// Eyebrow shared style
const EYEBROW = "text-xs font-medium tracking-[0.2em] uppercase mb-5";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const About = () => (
  <div className="min-h-screen bg-background text-foreground flex flex-col">
    <SEOHead
      title="About - Lumient"
      description="Lumient builds maintained operational records for document-driven businesses: source-bound answers, live state, and governed workflows for teams and agents."
      canonical="https://lumient.ai/about"
    />

    <main className="flex-1 w-full">

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="bg-background border-b border-border">

        <div className={`${CONTAINER} pt-28 pb-10 lg:pt-36 lg:pb-12`}>
          <motion.div
            className="grid grid-cols-12 items-start gap-y-8"
            variants={heroContainer}
            initial="hidden"
            animate="visible"
          >
            {/* Headline — 7 cols */}
            <div className="col-span-12 lg:col-span-7">
              <motion.h1
                variants={heroItem}
                className="font-serif font-semibold text-[clamp(3rem,5.5vw,5.75rem)] leading-[0.95] tracking-[-0.035em] text-[#0B0B0B]"
              >
                Why We're Here
              </motion.h1>
            </div>

            {/* Belief — 5 cols, auto-placed after headline, no col-start */}
            <motion.p
              variants={heroItem}
              className="col-span-12 lg:col-span-5 font-serif text-[clamp(1.25rem,1.8vw,1.65rem)] font-normal leading-[1.35] tracking-[-0.01em] text-[#0B0B0B]"
            >
              We believe enterprise AI has to be accountable before it can be useful.
            </motion.p>
          </motion.div>
        </div>

        {/* Contained atmospheric image */}
        <div className={`${CONTAINER} pb-10 lg:pb-14`}>
          <motion.div
            className="w-full overflow-hidden rounded-2xl"
            style={{ aspectRatio: "16 / 6" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: EASE }}
          >
            <img
              src="/media/about/alps.png"
              alt=""
              className="w-full h-full object-cover"
              style={{ objectPosition: "center 45%", filter: "saturate(0.82) contrast(0.95)" }}
            />
          </motion.div>
        </div>

      </section>

      {/* ── What we saw — HEADING LEFT ─────────────────────────────────────── */}
      <section className="bg-surface-subtle border-b border-border">
        <div className={`${CONTAINER} py-20 lg:py-28`}>
          <div className="grid grid-cols-12 items-start gap-y-12">

            {/* Anchor — narrow left col */}
            <div className="col-span-12 lg:col-span-3 lg:sticky lg:top-32 lg:self-start">
              <p className={EYEBROW} style={{ color: "oklch(var(--primary))" }}>What we saw</p>
              <h2 className="font-serif font-semibold text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-[#0B0B0B]">
                We kept seeing the same failure.
              </h2>
            </div>

            {/* Text — wide right col */}
            <div className="col-span-12 lg:col-span-8 lg:col-start-5 space-y-7">
              <p className="text-[1.5rem] font-light leading-[1.5] text-foreground">
                When we looked at how enterprises were actually working with documents, we saw
                AI tools that could read anything but left no maintained state, and extraction
                pipelines that produced results no system ever maintained.
              </p>
              <p className="text-[1.25rem] font-light leading-[1.7] text-muted-foreground">
                We saw agents deployed directly on raw files, answering the same question freshly
                every time it surfaced, and organizations where the same contract had been
                summarized by different people, in different tools, for different purposes, with
                no version ever becoming authoritative because none of them were connected to
                what happened next.
              </p>
              <p className="text-[1.25rem] font-normal leading-[1.7] text-foreground">
                We saw the same gap in every deployment, and we knew someone had to build the
                layer that was missing. That is why we built Lumient.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── The Standard — HEADING LEFT ────────────────────────────────────── */}
      <section className="bg-background border-b border-border">
        <div className={`${CONTAINER} py-20 lg:py-28`}>
          <div className="grid grid-cols-12 items-start gap-y-12">

            <div className="col-span-12 lg:col-span-3 lg:sticky lg:top-32 lg:self-start">
              <p className={EYEBROW} style={{ color: "oklch(var(--primary))" }}>The Standard</p>
              <h2 className="font-serif font-semibold text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-[#0B0B0B]">
                Access is not trust.
              </h2>
            </div>

            <div className="col-span-12 lg:col-span-8 lg:col-start-5 space-y-7">
              <p className="text-[1.5rem] font-light leading-[1.5] text-foreground">
                Giving an AI system access to documents is easy. Knowing what it used, what it
                changed, where an answer came from, and whether the underlying business state is
                current is the hard part.
              </p>
              <p className="text-[1.25rem] font-light leading-[1.7] text-muted-foreground">
                We built Lumient around that standard: evidence stays attached, records remain
                inspectable, workflows are explicit, and every update has history.
              </p>
              <p className="text-[1.25rem] font-normal leading-[1.7] text-foreground">
                That is the difference between a model that can read company data and a system
                a company can rely on.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── Team — HEADING LEFT ────────────────────────────────────────────── */}
      <section className="bg-surface-subtle border-b border-border">
        <div className={`${CONTAINER} py-20 lg:py-28`}>
          <div className="grid grid-cols-12 items-start gap-y-12">

            {/* Anchor — narrow left col */}
            <div className="col-span-12 lg:col-span-3 lg:sticky lg:top-32 lg:self-start">
              <p className={EYEBROW} style={{ color: "oklch(var(--primary))" }}>The Team</p>
              <h2 className="font-serif font-semibold text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-[#0B0B0B]">
                Built by people who kept hitting the same wall.
              </h2>
            </div>

            {/* Text — wide right col */}
            <div className="col-span-12 lg:col-span-8 lg:col-start-5 space-y-7">
              <p className="text-[1.5rem] font-light leading-[1.5] text-foreground">
                We are a small group of friends, researchers, and engineers who have worked
                together for over five years. Before Lumient, we built and studied systems where
                reliability depended on provenance: document understanding, workflow execution,
                applied AI, and enterprise infrastructure.
              </p>
              <p className="text-[1.25rem] font-light leading-[1.7] text-muted-foreground">
                We studied and did research in Switzerland. Lumient comes from the standard we
                kept needing ourselves: AI systems should leave behind state that can be
                inspected, trusted, and reused.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── Principles ────────────────────────────────────────────────────── */}
      <section className="bg-background border-b border-border">
        <div className={`${CONTAINER} py-20 lg:py-28`}>
          <div className="mb-14">
            <p className={EYEBROW} style={{ color: "oklch(var(--primary))" }}>Principles</p>
            <h2 className="font-serif font-semibold text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-[#0B0B0B]">
              What we believe
            </h2>
          </div>

          <div className="divide-y divide-border">
            {principles.map((principle) => (
              <div key={principle.number} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-x-4 gap-y-2 py-8 lg:grid-cols-12 lg:gap-x-8 lg:py-10">
                <p className="col-span-1 font-mono text-[0.75rem] tracking-[0.16em] pt-[0.3rem]" style={{ color: "oklch(var(--primary))" }}>
                  {principle.number}
                </p>
                <h3 className="col-span-1 min-w-0 lg:col-span-4 font-serif font-semibold text-[1.35rem] leading-[1.2] tracking-[-0.015em] text-[#0B0B0B]">
                  {principle.title}
                </h3>
                <p className="col-span-1 col-start-2 min-w-0 lg:col-span-7 lg:col-start-6 text-[0.95rem] font-light leading-[1.65] text-muted-foreground lg:mt-0">
                  {principle.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Where we're going — HEADING LEFT ───────────────────────────────── */}
      <section className="bg-surface-subtle border-b border-border">
        <div className={`${CONTAINER} py-20 lg:py-28`}>
          <div className="grid grid-cols-12 items-start gap-y-12">

            <div className="col-span-12 lg:col-span-3 lg:sticky lg:top-32 lg:self-start">
              <p className={EYEBROW} style={{ color: "oklch(var(--primary))" }}>Direction</p>
              <h2 className="font-serif font-semibold text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-[#0B0B0B]">
                Trust starts narrow.
              </h2>
            </div>

            <div className="col-span-12 lg:col-span-8 lg:col-start-5 space-y-7">
              <p className="text-[1.5rem] font-light leading-[1.5] text-foreground">
                The organizations that will get the most from AI are the ones that treat
                operational knowledge as infrastructure. Every document processed, every workflow
                completed, every record updated makes the next decision cheaper and more reliable.
              </p>
              <p className="text-[1.25rem] font-light leading-[1.7] text-muted-foreground">
                We are building toward a future where an enterprise's accumulated operational
                knowledge is as queryable and trustworthy as its financial data, where agents can
                take on document-heavy work because the state they need has already been
                maintained, and where the second workflow is faster than the first because the
                patterns and records from the first are already there.
              </p>
              <p className="text-[1.25rem] font-normal leading-[1.7] text-foreground">
                We are at the beginning of that.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="bg-surface-inverse">
        <div className={`${CONTAINER} py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8`}>
          <div>
            <h2 className="text-3xl font-normal text-surface-inverse-foreground tracking-tight mb-2">
              Stop rebuilding the same answer from documents.
            </h2>
            <p className="text-surface-inverse-muted text-base">
              We will walk through the records, evidence, and workflows your team needs to keep current.
            </p>
          </div>
          <a href="/contact" className="glow-button flex-shrink-0 px-7 py-3 text-sm font-semibold rounded-full whitespace-nowrap">
            Book a Demo
          </a>
        </div>
      </section>

    </main>
  </div>
);

export default About;
